import React from 'react';
import { CheckCircle, AlertCircle, XCircle, FolderOpen } from 'lucide-react';
import { UploadProgress, UploadResult } from '../../types/upload.types';

interface Props {
  progress: UploadProgress;
  result?: UploadResult;
  onRetry?: () => void;
  onReset?: () => void;
}

export const UploadStatus: React.FC<Props> = ({ progress, result, onRetry, onReset }) => {
  const { status, error, folderName, uploadedFiles, totalFiles } = progress;

  if (status === 'idle' || status === 'getting-credentials' || status === 'uploading') {
    return null;
  }

  // Success State
  if (status === 'completed') {
    return (
      <div className="bg-green-900/20 border border-green-400/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-medium text-green-400">Blur detection complete – upload finished!</div>
            <div className="text-green-400 text-sm mt-1">
              {uploadedFiles} of {totalFiles} images uploaded to S3
            </div>
            
            {folderName && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-green-900/30 rounded border border-green-400/20">
                <FolderOpen className="w-4 h-4 text-green-400" />
                <span className="text-xs font-mono text-green-400 break-all">
                  {folderName}
                </span>
              </div>
            )}

            {result?.failedFiles && result.failedFiles.length > 0 && (
              <div className="mt-3 p-2 bg-[#0CB4E4]/10 border border-[#0CB4E4]/30 rounded">
                <div className="text-sm font-medium text-[#0CB4E4]">
                  {result.failedFiles.length} file(s) failed to upload:
                </div>
                <ul className="mt-1 text-xs text-[#0CB4E4]/90 space-y-1">
                  {result.failedFiles.map((file, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="font-mono">{file.fileName}:</span>
                      <span>{file.error}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {onReset && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={onReset}
              className="px-4 py-2 text-sm text-green-400 hover:text-green-300 hover:bg-green-900/30 rounded-md transition-colors"
            >
              Upload More Images
            </button>
          </div>
        )}
      </div>
    );
  }

  // Error State
  if (status === 'error') {
    return (
      <div className="bg-red-900/20 border border-red-400/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-medium text-red-400">Upload failed</div>
            {error && (
              <div className="text-red-300 text-sm mt-1">{error}</div>
            )}
            
            <div className="mt-3 text-sm text-red-400">
              <div className="font-medium mb-1">Common solutions:</div>
              <ul className="space-y-1 text-xs">
                <li>• Check your internet connection</li>
                <li>• Ensure you're still logged in</li>
                <li>• Try reducing the number of images</li>
                <li>• Use only JPEG or HEIC image formats</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-4 flex gap-3">
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-4 py-2 bg-red-500/30 text-red-300 text-sm font-medium rounded-md hover:bg-red-500/50 transition-colors border border-red-500/30"
            >
              Retry Upload
            </button>
          )}
          {onReset && (
            <button
              onClick={onReset}
              className="px-4 py-2 bg-[#0CB4E4] text-[#161513] hover:bg-[#0CB4E4]/90 text-sm font-medium rounded-md transition-colors shadow-md"
            >
              Start Over
            </button>
          )}
        </div>
      </div>
    );
  }

  // Cancelled State
  if (status === 'cancelled') {
    return (
      <div className="bg-[#0CB4E4]/10 border border-[#0CB4E4]/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <XCircle className="w-5 h-5 text-[#0CB4E4] mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-medium text-[#0CB4E4]">Upload cancelled</div>
            <div className="text-[#0CB4E4]/90 text-sm mt-1">
              The upload was cancelled before completion.
            </div>
          </div>
        </div>

        {onReset && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={onReset}
              className="px-4 py-2 text-[#0CB4E4] hover:text-[#FDD49C] hover:bg-[#0CB4E4]/20 text-sm font-medium rounded-md transition-colors"
            >
              Start Over
            </button>
          </div>
        )}
      </div>
    );
  }

  return null;
};
